import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonService from '../../../services/common.service';
import { toast } from 'react-toastify';
import '../../../assets/styles/clarification_list.scss';

export default function ClarificationList() {
  const navigate = useNavigate();
  const [clarifications, setClarifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRFQs, setExpandedRFQs] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  const [selectedClarification, setSelectedClarification] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchClarifications();
  }, []);

  const fetchClarifications = async () => {
    try {
      setLoading(true);
      const response = await CommonService.get('/rfq-clarification/seller/my');
      
      if (response && response.success && response.data) {
        const clarificationsData = Array.isArray(response.data) ? response.data : [];
        // Ensure attachments are parsed if they're strings
        const parsedClarifications = clarificationsData.map(clarification => {
          if (clarification.attachments) {
            if (typeof clarification.attachments === 'string') {
              try {
                clarification.attachments = JSON.parse(clarification.attachments);
              } catch (e) {
                console.error('Error parsing attachments:', e);
                clarification.attachments = [];
              }
            }
            if (!Array.isArray(clarification.attachments)) {
              clarification.attachments = [];
            }
          }
          if (clarification.buyer_reply_attachments) {
            if (typeof clarification.buyer_reply_attachments === 'string') {
              try {
                clarification.buyer_reply_attachments = JSON.parse(clarification.buyer_reply_attachments);
              } catch (e) {
                console.error('Error parsing buyer_reply_attachments:', e);
                clarification.buyer_reply_attachments = [];
              }
            }
            if (!Array.isArray(clarification.buyer_reply_attachments)) {
              clarification.buyer_reply_attachments = [];
            }
          }
          return clarification;
        });
        setClarifications(parsedClarifications);
      } else {
        setClarifications([]);
      }
    } catch (error) {
      console.error('Error fetching clarifications:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch clarifications';
      toast.error(errorMessage);
      setClarifications([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'INR') => {
    if (!amount) return 'N/A';
    const currencySymbol = currency === 'INR' ? '₹' : currency === 'USD' ? '$' : currency;
    return `${currencySymbol}${amount.toLocaleString('en-IN')}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'sql-status-badge sql-status-pending';
      case 'responded':
        return 'sql-status-badge sql-status-accepted';
      case 'closed':
        return 'sql-status-badge sql-status-rejected';
      default:
        return 'sql-status-badge sql-status-pending';
    }
  };

  // Filter clarifications based on search query
  const filteredClarifications = clarifications.filter(clarification => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      `clar-${clarification.clarification_id}`.toLowerCase().includes(query) ||
      `req-${clarification.rfq_id}`.toLowerCase().includes(query) ||
      clarification.subject?.toLowerCase().includes(query) ||
      clarification.message?.toLowerCase().includes(query) ||
      clarification.product_name?.toLowerCase().includes(query) ||
      clarification.category_name?.toLowerCase().includes(query)
    );
  });

  // Group clarifications by RFQ, then by Product
  const groupClarificationsByRFQAndProduct = (clarificationsList) => {
    const grouped = {};
    clarificationsList.forEach((clarification) => {
      const rfqId = clarification.rfq_id;
      const productKey = `${clarification.product_name || 'Unknown Product'}_${clarification.category_name || ''}`;
      
      if (!grouped[rfqId]) {
        grouped[rfqId] = {
          rfqId: rfqId,
          rfqNumber: `REQ-${rfqId}`,
          products: {},
          totalClarifications: 0,
          firstClarification: clarification
        };
      }
      
      if (!grouped[rfqId].products[productKey]) {
        grouped[rfqId].products[productKey] = {
          productName: clarification.product_name || 'Unknown Product',
          categoryName: clarification.category_name || 'N/A',
          quantity: clarification.product_quantity,
          quantityUnit: clarification.quantity_unit,
          targetPriceMin: clarification.target_price_min,
          targetPriceMax: clarification.target_price_max,
          targetPriceCurrency: clarification.target_price_currency,
          clarifications: []
        };
      }
      
      grouped[rfqId].products[productKey].clarifications.push(clarification);
      grouped[rfqId].totalClarifications++;
    });
    
    // Convert to array and sort
    return Object.values(grouped).map(rfq => ({
      ...rfq,
      products: Object.values(rfq.products),
      totalProducts: Object.keys(rfq.products).length
    })).sort((a, b) => b.rfqId - a.rfqId);
  };

  const groupedData = groupClarificationsByRFQAndProduct(filteredClarifications);

  // Toggle RFQ expand/collapse
  const toggleRFQ = (rfqId) => {
    setExpandedRFQs((prev) => ({
      ...prev,
      [rfqId]: !prev[rfqId]
    }));
  };

  // Toggle Product expand/collapse
  const toggleProduct = (rfqId, productKey) => {
    const key = `${rfqId}_${productKey}`;
    setExpandedProducts((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Initialize all RFQs and Products as collapsed by default
  React.useEffect(() => {
    const allRFQsCollapsed = {};
    const allProductsCollapsed = {};
    groupedData.forEach((rfq) => {
      allRFQsCollapsed[rfq.rfqId] = false;
      rfq.products.forEach((product) => {
        const productKey = `${product.productName}_${product.categoryName}`;
        allProductsCollapsed[`${rfq.rfqId}_${productKey}`] = false;
      });
    });
    setExpandedRFQs((prev) => ({ ...allRFQsCollapsed, ...prev }));
    setExpandedProducts((prev) => ({ ...allProductsCollapsed, ...prev }));
  }, [groupedData.length]);

  const handleViewDetails = (clarification) => {
    setSelectedClarification(clarification);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedClarification(null);
  };

  return (
    <div className="clarification-list">
      {/* Main Content */}
      <div className="sql-main-content">
        <div className="sql-title-section">
          <h1 className="sql-main-title">My Clarifications</h1>
          <p className="sql-subtitle">View and manage all clarifications you have submitted to buyers.</p>
        </div>

        {/* Search Bar */}
        <div className="sql-search-container">
          <input
            type="text"
            className="sql-search-input"
            placeholder="Search by RFQ ID, Clarification ID, Subject, Product..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* RFQ Groups with Products and Clarifications */}
        <div className="sql-quotations-container">
          {loading ? (
            <div className="sql-loading-container">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="sql-loading-text">Loading clarifications...</p>
            </div>
          ) : groupedData.length > 0 ? (
            groupedData.map((rfqGroup) => {
              const isRFQExpanded = expandedRFQs[rfqGroup.rfqId] === true;

              return (
                <div key={rfqGroup.rfqId} className="sql-rfq-group">
                  {/* RFQ Header - Level 1 */}
                  <div className="sql-rfq-header-sticky">
                    <div className="sql-rfq-header-content">
                      <div className="sql-rfq-header-left">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="24" 
                          height="24" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          className="sql-rfq-icon"
                        >
                          <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                        </svg>
                        <div className="sql-rfq-info">
                          <div className="sql-rfq-number">{rfqGroup.rfqNumber}</div>
                          <div className="sql-rfq-meta">
                            <span>First Clarification: {formatDate(rfqGroup.firstClarification?.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="sql-rfq-header-stats">
                        <div className="sql-stat-item">
                          <span className="sql-stat-label">Products</span>
                          <span className="sql-stat-value">{rfqGroup.totalProducts}</span>
                        </div>
                        <div className="sql-stat-item">
                          <span className="sql-stat-label">Clarifications</span>
                          <span className="sql-stat-value">{rfqGroup.totalClarifications}</span>
                        </div>
                      </div>
                      <button 
                        className="sql-rfq-toggle-btn"
                        onClick={() => toggleRFQ(rfqGroup.rfqId)}
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          style={{ transform: isRFQExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
                        >
                          <path d="m18 15-6-6-6 6"></path>
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Products - Level 2 */}
                  {isRFQExpanded && (
                    <div className="sql-products-container">
                      {rfqGroup.products.map((product) => {
                        const productKey = `${product.productName}_${product.categoryName}`;
                        const isProductExpanded = expandedProducts[`${rfqGroup.rfqId}_${productKey}`] === true;
                        const respondedCount = product.clarifications.filter(c => c.buyer_reply).length;
                        
                        return (
                          <div key={productKey} className="sql-product-card">
                            {/* Product Header */}
                            <div 
                              className="sql-product-header"
                              onClick={() => toggleProduct(rfqGroup.rfqId, productKey)}
                            >
                              <div className="sql-product-header-left">
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="18" 
                                  height="18" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                  className="sql-product-chevron"
                                  style={{ transform: isProductExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
                                >
                                  <path d="m9 18 6-6-6-6"></path>
                                </svg>
                                <div className="sql-product-info">
                                  <div className="sql-product-name">{product.productName}</div>
                                  <div className="sql-product-details">
                                    <span>{product.categoryName}</span>
                                    {product.quantity && (
                                      <>
                                        <span className="sql-separator">•</span>
                                        <span>Qty: {product.quantity} {product.quantityUnit || ''}</span>
                                      </>
                                    )}
                                    {product.targetPriceMin && product.targetPriceMax && (
                                      <>
                                        <span className="sql-separator">•</span>
                                        <span>Target: {product.targetPriceCurrency || 'INR'} {product.targetPriceMin} - {product.targetPriceMax}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="sql-product-header-right">
                                <span className="sql-clarification-count-badge">
                                  {product.clarifications.length} {product.clarifications.length === 1 ? 'clarification' : 'clarifications'}
                                  {respondedCount > 0 && (
                                    <span className="sql-responded-badge">{respondedCount} replied</span>
                                  )}
                                </span>
                              </div>
                            </div>

                            {/* Clarifications Table - Level 3 */}
                            {isProductExpanded && (
                              <div className="sql-clarifications-table-container">
                                <table className="sql-clarifications-table">
                                  <thead>
                                    <tr>
                                      <th>Subject</th>
                                      <th>Date</th>
                                      <th>Status</th>
                                      <th>Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {product.clarifications.map((clarification) => (
                                      <tr 
                                        key={clarification.clarification_id}
                                        className={clarification.buyer_reply ? 'sql-row-responded' : 'sql-row-pending'}
                                      >
                                        <td className="sql-cell-subject">
                                          <div className="sql-subject-text" title={clarification.subject}>
                                            {clarification.subject || 'N/A'}
                                          </div>
                                        </td>
                                        <td className="sql-cell-date">
                                          {formatDate(clarification.created_at)}
                                        </td>
                                        <td className="sql-cell-status">
                                          <span className={getStatusBadgeClass(clarification.status)}>
                                            {clarification.buyer_reply ? 'Replied' : clarification.status || 'Pending'}
                                          </span>
                                        </td>
                                        <td className="sql-cell-action">
                                          <button 
                                            className="sql-action-btn sql-action-view"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewDetails(clarification);
                                            }}
                                          >
                                            View
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="sql-empty-state">
              <p>No clarifications found.</p>
            </div>
          )}
        </div>
      </div>

      {/* View Clarification Details Modal */}
      {showDetailsModal && selectedClarification && (
        <div className="brfq-modal-overlay" onClick={handleCloseModal}>
          <div className="brfq-view-request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="brfq-view-request-header">
              <div>
                <h2 className="brfq-view-request-title">Clarification Details</h2>
                <p className="brfq-view-request-subtitle">View details of your submitted clarification</p>
              </div>
              <button className="brfq-view-request-close" onClick={handleCloseModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>

            <div className="brfq-view-request-body">
              <div className="brfq-view-request-summary">
                <div className="brfq-view-request-summary-item">
                  <span className="brfq-view-request-summary-label">Clarification ID</span>
                  <p className="brfq-view-request-summary-value">CLAR-{selectedClarification.clarification_id}</p>
                </div>
                <div className="brfq-view-request-summary-item">
                  <span className="brfq-view-request-summary-label">RFQ ID</span>
                  <p className="brfq-view-request-summary-value">REQ-{selectedClarification.rfq_id}</p>
                </div>
                <div className="brfq-view-request-summary-item">
                  <span className="brfq-view-request-summary-label">Status</span>
                  <p className="brfq-view-request-summary-value">
                    <span className={getStatusBadgeClass(selectedClarification.status)}>
                      {selectedClarification.status || 'Pending'}
                    </span>
                  </p>
                </div>
                <div className="brfq-view-request-summary-item">
                  <span className="brfq-view-request-summary-label">Submitted On</span>
                  <p className="brfq-view-request-summary-value">
                    {formatDate(selectedClarification.created_at)}
                  </p>
                </div>
              </div>

              <div className="brfq-view-request-divider"></div>

              <div className="brfq-view-request-section">
                <div className="brfq-view-request-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                    <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                    <path d="M12 22V12"></path>
                    <polyline points="3.29 7 12 12 20.71 7"></polyline>
                    <path d="m7.5 4.27 9 5.15"></path>
                  </svg>
                  <h3 className="brfq-view-request-section-title">RFQ Product Details</h3>
                </div>
                <div className="brfq-view-request-grid">
                  <div className="brfq-view-request-grid-row-4">
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Product Name</label>
                      <p className="brfq-view-request-field-value">{selectedClarification.product_name || 'N/A'}</p>
                    </div>
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Category</label>
                      <p className="brfq-view-request-field-value">{selectedClarification.category_name || 'N/A'}</p>
                    </div>
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Quantity</label>
                      <p className="brfq-view-request-field-value">
                        {selectedClarification.product_quantity ? `${selectedClarification.product_quantity} ${selectedClarification.quantity_unit || ''}`.trim() : 'N/A'}
                      </p>
                    </div>
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Target Price</label>
                      <p className="brfq-view-request-field-value brfq-view-request-price">
                        {selectedClarification.target_price_min && selectedClarification.target_price_max
                          ? `${selectedClarification.target_price_currency || 'INR'} ${selectedClarification.target_price_min} - ${selectedClarification.target_price_max}`
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="brfq-view-request-divider"></div>

              <div className="brfq-view-request-section">
                <div className="brfq-view-request-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <path d="M14 2v6h6"></path>
                    <path d="M16 13H8"></path>
                    <path d="M16 17H8"></path>
                    <path d="M10 9H8"></path>
                  </svg>
                  <h3 className="brfq-view-request-section-title">Subject</h3>
                </div>
                <div className="brfq-view-request-grid">
                  <div className="brfq-view-request-field brfq-view-request-field-full">
                    <p className="brfq-view-request-field-value">{selectedClarification.subject}</p>
                  </div>
                </div>
              </div>

              <div className="brfq-view-request-divider"></div>

              <div className="brfq-view-request-section">
                <div className="brfq-view-request-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                    <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <h3 className="brfq-view-request-section-title">Message</h3>
                </div>
                <div className="brfq-view-request-grid">
                  <div className="brfq-view-request-field brfq-view-request-field-full">
                    <p className="brfq-view-request-description">{selectedClarification.message}</p>
                  </div>
                </div>
              </div>

              {selectedClarification.attachments && selectedClarification.attachments.length > 0 && (
                <>
                  <div className="brfq-view-request-divider"></div>
                  <div className="brfq-view-request-section">
                    <div className="brfq-view-request-section-header">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                        <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                      </svg>
                      <h3 className="brfq-view-request-section-title">Attachments</h3>
                    </div>
                    <div className="brfq-view-request-grid">
                      <div className="brfq-view-request-field brfq-view-request-field-full">
                        <div className="brfq-reject-attachments-list">
                          {selectedClarification.attachments.map((attachment, index) => {
                            const fileName = attachment.split('/').pop() || `Attachment ${index + 1}`;
                            const fileUrl = attachment.startsWith('http') ? attachment : `http://localhost:3000${attachment}`;
                            return (
                              <div key={index} className="brfq-reject-attachment-item">
                                <div className="brfq-attachment-left">
                                  <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="brfq-attachment-icon"
                                  >
                                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                                    <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                                  </svg>
                                  <div className="brfq-attachment-info">
                                    <div className="brfq-attachment-name">{fileName}</div>
                                  </div>
                                </div>
                                <div className="brfq-attachment-actions">
                                  <button 
                                    className="brfq-attachment-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      window.open(fileUrl, '_blank');
                                    }}
                                  >
                                    <svg 
                                      xmlns="http://www.w3.org/2000/svg" 
                                      width="16" 
                                      height="16" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2" 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round"
                                    >
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                      <circle cx="12" cy="12" r="3"></circle>
                                    </svg>
                                    View
                                  </button>
                                  <button 
                                    className="brfq-attachment-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      // Create a temporary anchor element to trigger download
                                      const link = document.createElement('a');
                                      link.href = fileUrl;
                                      link.download = fileName;
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <svg 
                                      xmlns="http://www.w3.org/2000/svg" 
                                      width="16" 
                                      height="16" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2" 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round"
                                    >
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                      <polyline points="7 10 12 15 17 10"></polyline>
                                      <line x1="12" x2="12" y1="15" y2="3"></line>
                                    </svg>
                                    Download
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Buyer Reply Section */}
              {selectedClarification.buyer_reply && (
                <>
                  <div className="brfq-view-request-divider"></div>
                  <div className="brfq-view-request-section">
                    <div className="brfq-view-request-section-header">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                        <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                      </svg>
                      <h3 className="brfq-view-request-section-title">Buyer Reply</h3>
                    </div>
                    <div className="brfq-view-request-grid">
                      <div className="brfq-view-request-field brfq-view-request-field-full">
                        <p className="brfq-view-request-description">{selectedClarification.buyer_reply}</p>
                      </div>
                      {selectedClarification.buyer_reply_date && (
                        <div className="brfq-view-request-field">
                          <label className="brfq-view-request-field-label">Replied On</label>
                          <p className="brfq-view-request-field-value">{formatDate(selectedClarification.buyer_reply_date)}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Buyer Reply Attachments */}
                  {selectedClarification.buyer_reply_attachments && 
                   Array.isArray(selectedClarification.buyer_reply_attachments) && 
                   selectedClarification.buyer_reply_attachments.length > 0 && (
                    <>
                      <div className="brfq-view-request-divider"></div>
                      <div className="brfq-view-request-section">
                        <div className="brfq-view-request-section-header">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                          </svg>
                          <h3 className="brfq-view-request-section-title">Buyer Reply Attachments</h3>
                        </div>
                        <div className="brfq-view-request-grid">
                          <div className="brfq-view-request-field brfq-view-request-field-full">
                            <div className="brfq-reject-attachments-list">
                              {selectedClarification.buyer_reply_attachments.map((attachment, index) => {
                                const fileName = attachment.split('/').pop() || `Attachment ${index + 1}`;
                                const fileUrl = attachment.startsWith('http') ? attachment : `http://localhost:3000${attachment}`;
                                return (
                                  <div key={index} className="brfq-reject-attachment-item">
                                    <div className="brfq-attachment-left">
                                      <svg 
                                        xmlns="http://www.w3.org/2000/svg" 
                                        width="20" 
                                        height="20" 
                                        viewBox="0 0 24 24" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                        className="brfq-attachment-icon"
                                      >
                                        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                                        <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                                      </svg>
                                      <div className="brfq-attachment-info">
                                        <div className="brfq-attachment-name">{fileName}</div>
                                      </div>
                                    </div>
                                    <div className="brfq-attachment-actions">
                                      <button 
                                        className="brfq-attachment-btn"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          window.open(fileUrl, '_blank');
                                        }}
                                      >
                                        <svg 
                                          xmlns="http://www.w3.org/2000/svg" 
                                          width="16" 
                                          height="16" 
                                          viewBox="0 0 24 24" 
                                          fill="none" 
                                          stroke="currentColor" 
                                          strokeWidth="2" 
                                          strokeLinecap="round" 
                                          strokeLinejoin="round"
                                        >
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                          <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                        View
                                      </button>
                                      <button 
                                        className="brfq-attachment-btn"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          // Create a temporary anchor element to trigger download
                                          const link = document.createElement('a');
                                          link.href = fileUrl;
                                          link.download = fileName;
                                          link.target = '_blank';
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }}
                                      >
                                        <svg 
                                          xmlns="http://www.w3.org/2000/svg" 
                                          width="16" 
                                          height="16" 
                                          viewBox="0 0 24 24" 
                                          fill="none" 
                                          stroke="currentColor" 
                                          strokeWidth="2" 
                                          strokeLinecap="round" 
                                          strokeLinejoin="round"
                                        >
                                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                          <polyline points="7 10 12 15 17 10"></polyline>
                                          <line x1="12" x2="12" y1="15" y2="3"></line>
                                        </svg>
                                        Download
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
